import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getBaseUrl } from "../config";
import { getAuthApiHeaders } from "../../utils/common";
import { showSnackbar } from "./snackbar";

const baseUrl = getBaseUrl();

const kpiRequestHeaders = () => ({
  "Content-Type": "application/json",
  "use-case": "src",
  ...getAuthApiHeaders(),
  client: "UT",
});

function createWidgetThunk(typePrefix, path) {
  return createAsyncThunk(
    `kpiWidgetMetrics/${typePrefix}`,
    async (_, { rejectWithValue, dispatch }) => {
      try {
        const response = await axios.get(`${baseUrl}${path}`, {
          headers: kpiRequestHeaders(),
          withCredentials: true,
        });
        return response.data;
      } catch (error) {
        dispatch(
          showSnackbar({ message: "Failed to load!", severity: "error" }),
        );
        return rejectWithValue(
          error.response?.statusText ?? error.message ?? "Error",
        );
      }
    },
  );
}

/** My Performance / Start Tracking card */
export const fetchKpiMyPerformance = createWidgetThunk(
  "fetchMyPerformance",
  "/kpi_metrics/my_performance",
);

/** National & regional territory tables */
export const fetchKpiNationalRegional = createWidgetThunk(
  "fetchNationalRegional",
  "/kpi_metrics/national_regional",
);

/** Pending referrals summary + HCP breakdown */
export const fetchKpiPendingReferrals = createWidgetThunk(
  "fetchPendingReferrals",
  "/kpi_metrics/pending_referrals",
);

/** HCP growers / decliners */
export const fetchKpiHcpGrowers = createWidgetThunk(
  "fetchHcpGrowers",
  "/kpi_metrics/hcp_growers",
);

/** Key insights counts + category rows */
export const fetchKpiInsights = createWidgetThunk(
  "fetchInsights",
  "/kpi_metrics/insights",
);

/** Reach & frequency by decile tier */
export const fetchKpiReachFrequency = createWidgetThunk(
  "fetchReachFrequency",
  "/kpi_metrics/reach_frequency",
);

/** Treatment rate and days to therapy by referral month */
export const fetchKpiTreatmentRate = createWidgetThunk(
  "fetchTreatmentRate",
  "/kpi_metrics/treatment_rate",
);

/** Top Yutrepia writers (current vs prior month) */
export const fetchKpiTopYutrepiaWriters = createWidgetThunk(
  "fetchTopYutrepiaWriters",
  "/kpi_metrics/top_yutrepia_writers",
);

/** Weekly HCP/HCO account roster changes */
export const fetchKpiHcpHcoAccountChanges = createWidgetThunk(
  "fetchHcpHcoAccountChanges",
  "/kpi_metrics/hcp_hco_account_changes",
);

/** De novo writers for the logged-in rep */
export const fetchKpiDeNovo = createWidgetThunk(
  "fetchDeNovo",
  "/kpi_metrics/denovo",
);

/** Quarterly business review report sections */
export const fetchKpiQbrReport = createWidgetThunk(
  "fetchQbrReport",
  "/kpi_metrics/qbr_report",
);

/** Tyvaso DPI dosage counts by HCP and dose */
export const fetchKpiTyvasoDpiDosageDistribution = createWidgetThunk(
  "fetchTyvasoDpiDosageDistribution",
  "/kpi_metrics/tyvaso_dpi_dosage_distribution",
);

/** Tyvaso DPI dosage alerts for the logged-in user */
export const fetchKpiTyvasoDpiDosageNotification = createWidgetThunk(
  "fetchTyvasoDpiDosageNotification",
  "/kpi_metrics/tyvaso_dpi_dosage_notification",
);

const emptyWidget = () => ({
  data: null,
  isLoading: false,
  isError: false,
  error: null,
});

const initialState = {
  myPerformance: emptyWidget(),
  nationalRegional: emptyWidget(),
  pendingReferrals: emptyWidget(),
  hcpGrowers: emptyWidget(),
  insights: emptyWidget(),
  reachFrequency: emptyWidget(),
  treatmentRate: emptyWidget(),
  topYutrepiaWriters: emptyWidget(),
  hcpHcoAccountChanges: emptyWidget(),
  deNovo: emptyWidget(),
  qbrReport: emptyWidget(),
  tyvasoDpiDosageDistribution: emptyWidget(),
  tyvasoDpiDosageNotification: emptyWidget(),
};

function wireWidget(builder, thunk, stateKey) {
  builder
    .addCase(thunk.pending, (state) => {
      state[stateKey].isLoading = true;
      state[stateKey].isError = false;
      state[stateKey].error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[stateKey].isLoading = false;
      state[stateKey].data = action.payload;
      state[stateKey].isError = false;
      state[stateKey].error = null;
    })
    .addCase(thunk.rejected, (state, action) => {
      state[stateKey].isLoading = false;
      state[stateKey].isError = true;
      state[stateKey].error = action.payload ?? null;
    });
}

const kpiWidgetMetricsSlice = createSlice({
  name: "kpiWidgetMetrics",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    wireWidget(builder, fetchKpiMyPerformance, "myPerformance");
    wireWidget(builder, fetchKpiNationalRegional, "nationalRegional");
    wireWidget(builder, fetchKpiPendingReferrals, "pendingReferrals");
    wireWidget(builder, fetchKpiHcpGrowers, "hcpGrowers");
    wireWidget(builder, fetchKpiInsights, "insights");
    wireWidget(builder, fetchKpiReachFrequency, "reachFrequency");
    wireWidget(builder, fetchKpiTreatmentRate, "treatmentRate");
    wireWidget(builder, fetchKpiTopYutrepiaWriters, "topYutrepiaWriters");
    wireWidget(builder, fetchKpiHcpHcoAccountChanges, "hcpHcoAccountChanges");
    wireWidget(builder, fetchKpiDeNovo, "deNovo");
    wireWidget(builder, fetchKpiQbrReport, "qbrReport");
    wireWidget(
      builder,
      fetchKpiTyvasoDpiDosageDistribution,
      "tyvasoDpiDosageDistribution",
    );
    wireWidget(
      builder,
      fetchKpiTyvasoDpiDosageNotification,
      "tyvasoDpiDosageNotification",
    );
  },
});

export default kpiWidgetMetricsSlice.reducer;
